// GameplayAudioAdapter — the single bridge between authoritative gameplay events and the
// audio engine. Components never call playback directly; they dispatch normalized events here.
// Critical cues (outcome/danger/rank/prestige/achievement) require server confirmation.
import audioManager from "./AudioManager";
import { track } from "../api";
import { eventToAudioState, normalizeDanger, dangerToState } from "./audioStateSelectors";
import { AudioEventDeduplicator, cueKey } from "./AudioEventDeduplicator";
import { GameplayEvent, SERVER_CONFIRMED, ONE_TIME_CUES, EVENT_PRIORITY, CREW_EVENTS, CREW_EVENT_SET } from "./gameplayAudioEvents";

const AGGREGATE_WINDOW_MS = 2500;
const LOG_MAX = 40;
const IGNORED_MAX = 15;
const MAX_MOTIFS = 3;

class GameplayAudioAdapter {
  constructor() {
    if (GameplayAudioAdapter._i) return GameplayAudioAdapter._i;
    this.dedup = new AudioEventDeduplicator();
    this.activeGameplay = false;
    this.suspended = false;
    this.currentRoute = null;
    this.currentExpedition = null;
    this.authoritativeState = null;
    this.fallbackState = "landing";
    this.dangerLevel = "calm";
    this.dangerScore = 0;
    this.dangerVersion = null;
    this.dangerReasons = [];
    this.prevDangerLevel = null;
    this.lastDangerTrigger = null;
    this.lastDangerAt = null;
    this.dangerSuppressed = false;
    this.fallbackUsed = false;
    this.inChallenge = false;
    this.activeCrewMotifs = [];
    this._crewCd = {};
    // crew_injured also drives the recovery_camp base state, so it flows through the generic
    // path (with an added injury-motif layer). All other crew events are motif-only.
    this._crewMotifOnly = new Set([...CREW_EVENT_SET].filter((t) => t !== GameplayEvent.CREW_INJURED));
    this.lastEvent = null;
    this.lastServerEvent = null;
    this.lastCueKey = null;
    this.dupCount = 0;
    this.transitionFailures = 0;
    this.eventLog = [];
    this.ignored = [];
    this._lastCueAt = 0;
    this._lastCuePriority = 0;
    this._sub = null;
    GameplayAudioAdapter._i = this;
  }

  subscribe(cb) { this._sub = cb; return () => { this._sub = null; }; }
  _emit() { if (this._sub) try { this._sub(this.snapshot()); } catch (e) { /* noop */ } }

  _record(evt) {
    this.lastEvent = { type: evt.type, at: Date.now() };
    if (SERVER_CONFIRMED.has(evt.type) && evt.confirmed === true) this.lastServerEvent = this.lastEvent;
    this.eventLog.push({ type: evt.type, confirmed: !!evt.confirmed, at: Date.now() });
    if (this.eventLog.length > LOG_MAX) this.eventLog.shift();
  }
  _ignore(evt, reason) {
    this.ignored.push({ type: evt.type, reason, at: Date.now() });
    if (this.ignored.length > IGNORED_MAX) this.ignored.shift();
    track("gameplay_audio_event_ignored");
    this._emit();
  }
  _addMotif(id) {
    if (!id || this.activeCrewMotifs.includes(id)) return;
    this.activeCrewMotifs.push(id);
    if (this.activeCrewMotifs.length > MAX_MOTIFS) this.activeCrewMotifs.shift();
  }

  handle(evt) {
    if (!evt || !evt.type) return { ignored: true };
    this._record(evt);

    if (evt.type === GameplayEvent.ROUTE_ENTERED) {
      this.currentRoute = evt.route || null;
      this.fallbackState = evt.state || "dashboard";
      // active server-confirmed gameplay takes priority over route music
      if (!this.activeGameplay && audioManager.canActivate()) audioManager.playState(evt.state, null);
      else audioManager.transitionTo(evt.state, null);
      this._emit();
      return { fallback: true, deferred: this.activeGameplay };
    }

    if (SERVER_CONFIRMED.has(evt.type) && evt.confirmed !== true) {
      this._ignore(evt, "unconfirmed_server_event");
      return { ignored: true };
    }

    // motif-only crew events layer over the base soundtrack (never change the base state)
    if (this._crewMotifOnly.has(evt.type)) return this._crewMotif(evt);

    const sel = eventToAudioState(evt);
    if (!sel.state) { this._ignore(evt, "no_mapping"); return { ignored: true }; }
    if (evt.crew_id) this._addMotif(evt.crew_id);
    if (sel.expedition) this.currentExpedition = sel.expedition;

    if (evt.type === GameplayEvent.CONNECTION_LOST) {
      audioManager.fadeOffline(); this.suspended = true;
      audioManager.stopAllCrew(); this.activeCrewMotifs = [];
      track("gameplay_audio_challenge_stopped"); this._emit();
      return { offline: true };
    }
    if (evt.type === GameplayEvent.SESSION_STARTED) this.activeGameplay = true;
    if (evt.type === GameplayEvent.SKILL_CHALLENGE_STARTED) this.inChallenge = true;
    if (evt.type === GameplayEvent.SKILL_CHALLENGE_COMPLETED) this.inChallenge = false;

    // ---- server-authoritative danger signal ----
    if (evt.type === GameplayEvent.DANGER_LEVEL_CHANGED) {
      const level = normalizeDanger(evt.level);
      this.prevDangerLevel = this.dangerLevel;
      this.dangerLevel = level;
      this.dangerScore = evt.score != null ? evt.score : this.dangerScore;
      this.dangerVersion = evt.version || this.dangerVersion;
      this.dangerReasons = Array.isArray(evt.reasons) ? evt.reasons.slice(0, 4) : [];
      this.fallbackUsed = !!evt.fallback;
      this.lastDangerTrigger = evt.trigger_event || "danger_level_changed";
      this.lastDangerAt = Date.now();
      const order = { calm: 0, alert: 1, danger: 2, critical: 3 };
      if (this.prevDangerLevel && this.prevDangerLevel !== level) {
        track("danger_state_changed");
        track(order[level] > order[this.prevDangerLevel] ? "danger_state_increased" : "danger_state_decreased");
      }
      const s = audioManager.settings || {};
      const isTense = level === "danger" || level === "critical";
      // preferences applied AFTER the authoritative level is known
      if (s.dynamic_tension_enabled === false && isTense) {
        this.dangerSuppressed = true;
        track("danger_audio_transition_suppressed");
        // keep the expedition's base theme; do not add escalating tension
        if (!this.inChallenge && audioManager.canActivate()) audioManager.playState("expedition_exploration", this.currentExpedition || null);
      } else if (this.inChallenge) {
        // skill challenge remains the primary active state; danger only recorded
        this.dangerSuppressed = false;
      } else {
        this.dangerSuppressed = false;
        this.authoritativeState = dangerToState(level);
        const r = audioManager.playState(this.authoritativeState, this.currentExpedition || null);
        if (r && r.played === false) this.transitionFailures += 1;
        track("danger_audio_transition_applied");
      }
      this._emit();
      return { danger: level, suppressed: this.dangerSuppressed };
    }

    // one-time ceremonial / result cues → dedup + priority aggregation
    if (ONE_TIME_CUES.has(evt.type)) {
      const key = cueKey(evt);
      if (this.dedup.has(key)) {
        this.dupCount += 1; track("gameplay_audio_duplicate_cue_prevented"); this._emit();
        return { duplicate: true };
      }
      const pr = EVENT_PRIORITY[evt.type] || 0;
      const now = Date.now();
      if (now - this._lastCueAt < AGGREGATE_WINDOW_MS && pr < this._lastCuePriority) {
        this._ignore(evt, "lower_priority_aggregated");
        return { aggregated: true };
      }
      this.dedup.add(key);
      this.lastCueKey = key; this._lastCueAt = now; this._lastCuePriority = pr;
      this.authoritativeState = sel.state;
      const r = audioManager.playState(sel.state, sel.expedition || this.currentExpedition || null);
      if (r && r.played === false) this.transitionFailures += 1;
      track("gameplay_audio_outcome_cue_played");
      if (evt.type === GameplayEvent.OUTCOME_CONFIRMED) { this.activeGameplay = false; this.inChallenge = false; }
      this._emit();
      return { played: true, state: sel.state };
    }

    // ambient / loop transition (server-confirmed non-cue or allowed neutral)
    this.authoritativeState = sel.state;
    const r = audioManager.playState(sel.state, sel.expedition || this.currentExpedition || null);
    if (r && r.played === false) this.transitionFailures += 1;
    // crew_injured also lays a restrained injury motif over the recovery-camp base state
    if (evt.type === GameplayEvent.CREW_INJURED && evt.crew_id) this._crewMotif(evt, "injury");
    track("gameplay_audio_state_changed");
    this._emit();
    return r || {};
  }

  // Layer a decorative crew motif. Safety/critical/challenge states stay primary; one-time crew
  // cues are deduplicated across refresh/reconnect/resume; at most 3 recognizable motifs at once.
  _crewMotif(evt, roleOverride) {
    const spec = CREW_EVENTS[evt.type] || {};
    const role = roleOverride || spec.role || "base";
    if (!evt.crew_id) { this._ignore(evt, "missing_crew_id"); return { ignored: true }; }
    if (this.inChallenge || this.dangerLevel === "critical") {
      track("crew_motif_suppressed"); this._emit(); return { suppressed: true, reason: "primary_state" };
    }
    if (spec.oneTime !== false) {                 // default: safe one-time dedup
      const key = `crew:${evt.type}:${evt.crew_id}:${evt.session_id || ""}:${evt.event_version || "v1"}`;
      if (this.dedup.has(key)) { this.dupCount += 1; track("crew_motif_deduplicated"); this._emit(); return { duplicate: true }; }
      this.dedup.add(key);
    } else {                                      // selection: short per-crew cooldown
      const now = Date.now();
      if (now - (this._crewCd[evt.crew_id] || 0) < 1500) { track("crew_motif_suppressed"); this._emit(); return { cooldown: true }; }
      this._crewCd[evt.crew_id] = now;
    }
    if (this.activeCrewMotifs.length >= MAX_MOTIFS && !this.activeCrewMotifs.includes(evt.crew_id)) {
      track("crew_motif_priority_resolved"); this._emit(); return { ensemble: true };  // no 4th distinct melody
    }
    const supporting = this.activeCrewMotifs.length >= 1;
    this._addMotif(evt.crew_id);
    Promise.resolve(audioManager.playCrewMotif(evt.crew_id, role, { supporting })).then((res) => {
      if (res && res.played) track("crew_motif_played");
      else if (res && res.fallback) track("crew_motif_fallback_used");
      else track("crew_motif_suppressed");
      this._emit();
    }).catch(() => track("crew_motif_fallback_used"));
    track("crew_motif_priority_resolved");
    this._emit();
    return { crew: evt.crew_id, role, supporting };
  }

  // Called by the provider after a verified reconnect (entitlement revalidated + session fetched).
  async restore(state, expedition) {
    this.suspended = false;
    const st = state || this.authoritativeState || this.fallbackState;
    const r = await audioManager.restoreOnline(st, expedition || this.currentExpedition || null);
    if (r && r.restored !== false) track("gameplay_audio_restore_completed");
    else track("gameplay_audio_restore_failed");
    this._emit();
    return r;
  }

  clearSession() { this.activeGameplay = false; this.activeCrewMotifs = []; audioManager.stopAllCrew(); this._emit(); }

  snapshot() {
    return {
      currentRoute: this.currentRoute,
      sessionActive: this.activeGameplay,
      suspended: this.suspended,
      lastEvent: this.lastEvent,
      lastServerEvent: this.lastServerEvent,
      authoritativeState: this.authoritativeState,
      fallbackState: this.fallbackState,
      dangerLevel: this.dangerLevel,
      dangerScore: this.dangerScore,
      dangerVersion: this.dangerVersion,
      dangerReasons: [...this.dangerReasons],
      prevDangerLevel: this.prevDangerLevel,
      lastDangerTrigger: this.lastDangerTrigger,
      lastDangerAt: this.lastDangerAt,
      dangerSuppressed: this.dangerSuppressed,
      fallbackUsed: this.fallbackUsed,
      inChallenge: this.inChallenge,
      expeditionTheme: this.currentExpedition,
      activeCrewMotifs: [...this.activeCrewMotifs],
      lastCueKey: this.lastCueKey,
      dedupSize: this.dedup.size(),
      dupPrevented: this.dupCount,
      transitionFailures: this.transitionFailures,
      ignored: [...this.ignored].slice(-8),
      eventLog: [...this.eventLog].slice(-12),
    };
  }
}

export const gameplayAudioAdapter = new GameplayAudioAdapter();
export default gameplayAudioAdapter;
